import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { PLANS, PlanSummary, CheckoutHeader, money, effectivePricing } from './checkout/PlanData';
import { submitMembershipEnrollment } from '../lib/api';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const Field = ({ label, htmlFor, error, children, span = 'full' }) => (
  <div className={span === 'full' ? 'col-span-2' : 'col-span-2 sm:col-span-1'}>
    <label htmlFor={htmlFor} className="block text-[10px] tracking-[0.25em] font-bold text-zinc-400 uppercase mb-1.5">
      {label}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
  </div>
);

const baseInput =
  'w-full bg-black/60 border border-red-900/40 rounded px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-600/30 transition';

export default function MembershipInfo() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const plan = PLANS[planId];

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', dob: '',
    street: '', city: '', state: '', zip: '',
    emName: '', emPhone: '', emRel: '',
    cardholder: '', cardNumber: '', cardExp: '', cardCvv: '', billingZip: '',
    agree: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showRenewal, setShowRenewal] = useState(false);
  const [successId, setSuccessId] = useState(null);
  const [selections, setSelections] = useState(null);

  // Restore native cursor on these pages — WebsiteFx (custom red cursor) is
  // only mounted on the homepage, so without this override the global
  // `body { cursor: none }` rule leaves the cursor invisible here.
  useEffect(() => {
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'auto';
    return () => { document.body.style.cursor = prev; };
  }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`checkout:${planId}`);
      if (raw) setSelections(JSON.parse(raw));
    } catch (_) { /* no-op */ }
  }, [planId]);

  const firstDueDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  }, []);

  const members = selections?.members || 1;
  const ptItem = selections?.pt || null;
  const addOnsTotal = useMemo(() => {
    if (!plan || !selections) return 0;
    return plan.addOns
      .filter((a) => (selections.addOns || []).includes(a.id))
      .reduce((acc, a) => acc + a.price, 0);
  }, [plan, selections]);

  // Live total for the sticky CTA — matches the "Due Today" panel above.
  const liveDueToday = useMemo(() => {
    if (!plan) return 0;
    const { signUpDues } = effectivePricing(plan, members);
    return Math.max(0, (plan.enrollmentFee || 0) + signUpDues + (ptItem?.price || 0) + addOnsTotal);
  }, [plan, members, ptItem, addOnsTotal]);

  if (!plan) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center bg-zinc-950 border border-red-900/40 rounded-md p-10 max-w-md">
          <h1 className="font-display text-2xl text-white mb-3">Plan not found</h1>
          <Link to="/#memberships" className="inline-block px-6 py-3 bg-red-600 hover:bg-red-500 text-white text-xs tracking-[0.25em] font-bold rounded">
            BACK TO PLANS
          </Link>
        </div>
      </div>
    );
  }

  const set = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((cur) => ({ ...cur, [key]: v }));
    if (errors[key]) setErrors((cur) => ({ ...cur, [key]: null }));
  };

  const onCardNumber = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
    setForm((cur) => ({ ...cur, cardNumber: digits.replace(/(\d{4})(?=\d)/g, '$1 ') }));
    if (errors.cardNumber) setErrors((cur) => ({ ...cur, cardNumber: null }));
  };
  const onCardExp = (e) => {
    let d = e.target.value.replace(/\D/g, '').slice(0, 4);
    if (d.length >= 3) d = `${d.slice(0, 2)}/${d.slice(2)}`;
    setForm((cur) => ({ ...cur, cardExp: d }));
  };
  const onCardCvv = (e) => {
    setForm((cur) => ({ ...cur, cardCvv: e.target.value.replace(/\D/g, '').slice(0, 4) }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.phone.trim() || form.phone.replace(/\D/g, '').length < 7) e.phone = 'Enter a valid phone';
    if (!form.street.trim()) e.street = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    if (!form.state.trim()) e.state = 'Required';
    if (!form.zip.trim() || form.zip.replace(/\D/g, '').length < 5) e.zip = 'Enter a valid ZIP';
    const anyCard = form.cardholder || form.cardNumber || form.cardExp || form.cardCvv || form.billingZip;
    if (anyCard) {
      if (!form.cardholder.trim()) e.cardholder = 'Required';
      if (form.cardNumber.replace(/\s/g, '').length < 13) e.cardNumber = 'Enter a valid card number';
      if (!/^\d{2}\/\d{2}$/.test(form.cardExp)) e.cardExp = 'MM/YY';
      if (form.cardCvv.length < 3) e.cardCvv = 'CVV';
      if (!form.billingZip.trim()) e.billingZip = 'Required';
    }
    if (!form.agree) e.agree = 'You must accept the terms to continue';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setSubmitting(true);
    try {
      const last4 = form.cardNumber ? form.cardNumber.replace(/\s/g, '').slice(-4) : null;
      const { signUpDues, monthlyDues } = effectivePricing(plan, members);
      const computedDueToday = (plan.enrollmentFee || 0) + signUpDues + (ptItem?.price || 0) + addOnsTotal;
      const computedMonthly = monthlyDues + (monthlyDues > 0 ? addOnsTotal : 0);
      const payload = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        date_of_birth: form.dob || null,
        address: { street: form.street.trim(), city: form.city.trim(), state: form.state.trim(), zip: form.zip.trim() },
        emergency: form.emName || form.emPhone
          ? { name: form.emName || null, phone: form.emPhone || null, relationship: form.emRel || null }
          : null,
        cardholder_name: form.cardholder || null,
        card_last4: last4,
        card_exp: form.cardExp || null,
        billing_zip: form.billingZip || null,
        selections: {
          plan_id: planId,
          plan_title: `${plan.title}${members > 1 ? ` (${members} members)` : ''}`,
          pt: ptItem,
          add_ons: selections?.addOns || [],
          due_today: selections?.dueToday ?? computedDueToday,
          monthly: selections?.monthly ?? computedMonthly,
          promo: selections?.promo || null,
        },
        agreed_terms: !!form.agree,
      };
      const res = await submitMembershipEnrollment(payload);
      setSuccessId(res.id);
      toast.success('Enrollment received! Welcome to FitnessPro Galt.');
      try { sessionStorage.removeItem(`checkout:${planId}`); } catch (_) { /* no-op */ }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not submit enrollment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (successId) {
    return (
      <div className="min-h-screen bg-black text-white font-sans">
        <CheckoutHeader />
        <main className="max-w-[720px] mx-auto px-6 py-20 text-center">
          <div className="bg-zinc-950 border border-red-900/40 rounded-md p-10 shadow-[0_0_60px_rgba(239,43,43,0.15)]" data-testid="enrollment-success">
            <CheckCircle2 className="w-16 h-16 text-red-500 mx-auto mb-5" />
            <h1 className="font-display text-3xl text-white mb-3">Welcome to FitnessPro!</h1>
            <p className="text-zinc-300 text-sm leading-relaxed mb-2">
              Your <span className="font-bold text-white">{plan.title}</span>{members > 1 ? ` (${members} members)` : ''} enrollment has been received.
            </p>
            <p className="text-zinc-500 text-xs tracking-[0.25em] uppercase mb-8">
              Confirmation ID: <span className="text-red-400 font-mono">{successId.slice(0, 8).toUpperCase()}</span>
            </p>
            <div className="text-left bg-black/60 border border-red-900/40 rounded p-5 mb-8 text-sm">
              <p className="text-zinc-300 leading-relaxed">
                A member of our team will reach out within 24 hours at <span className="font-bold text-white">{form.email}</span> to
                schedule your first visit and confirm billing. If you have questions, call us at <span className="font-bold text-red-400">(209) 745-5382</span>.
              </p>
            </div>
            <Link to="/" className="inline-block px-8 py-3.5 bg-red-600 hover:bg-red-500 text-white text-xs tracking-[0.3em] font-bold rounded transition">
              BACK TO HOMEPAGE
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-sans overflow-x-hidden max-w-full" data-testid="info-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[60%] h-[40%] bg-red-700/10 blur-[140px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-[40%] h-[30%] bg-red-700/8 blur-[120px] rounded-full" />
      </div>

      <div className="relative">
        <CheckoutHeader backTo="/#memberships" backLabel="BACK TO OPTIONS" />

        <main className="max-w-[1280px] mx-auto px-4 sm:px-6 py-8 sm:py-10 pb-32 overflow-x-hidden">
          {/* Progress indicator */}
          <ol className="flex items-center justify-center gap-2 sm:gap-4 mb-8 text-[10px] sm:text-[11px] tracking-[0.25em] font-bold">
            <li className="flex items-center gap-2 text-zinc-500">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-zinc-800 text-white">1</span>
              <span className="hidden sm:inline">PLAN OPTIONS</span>
            </li>
            <li className="w-6 sm:w-10 h-px bg-red-900/60" />
            <li className="flex items-center gap-2 text-red-400">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white shadow-[0_0_18px_rgba(239,43,43,0.55)]">2</span>
              <span>MEMBER INFO</span>
            </li>
            <li className="w-6 sm:w-10 h-px bg-zinc-800" />
            <li className="flex items-center gap-2 text-zinc-500">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-zinc-800 text-white">3</span>
              <span className="hidden sm:inline">CONFIRMATION</span>
            </li>
          </ol>

          <div className="grid lg:grid-cols-[360px_1fr] gap-8 items-start">
            <PlanSummary
              plan={plan}
              members={members}
              ptItem={ptItem}
              addOnsTotal={addOnsTotal}
              addOnsLabel={plan.monthlyDues > 0 ? 'Add-Ons (1st month)' : 'Add-Ons'}
              promo={selections?.promo}
              promoApplied={!!selections?.promoApplied}
              promoDiscount={selections?.promoDiscount || 0}
              firstDueDate={firstDueDate}
              showRenewal={showRenewal}
              onToggleRenewal={() => setShowRenewal((s) => !s)}
              testIdPrefix="info"
            />

            <section className="bg-zinc-950 border border-red-900/40 rounded-md overflow-hidden" data-testid="info-form">
              <h2 className="text-center text-[15px] font-bold text-white py-4 border-b border-red-900/40 tracking-wide">
                Member Information
              </h2>

              <div className="p-6 sm:p-8 pb-8 space-y-10">
                {/* PERSONAL */}
                <div>
                  <h3 className="text-[11px] font-bold tracking-[0.3em] text-red-500 uppercase mb-4">Personal Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="First Name" htmlFor="fn" error={errors.firstName} span="half">
                      <input id="fn" data-testid="info-firstname" value={form.firstName} onChange={set('firstName')} className={baseInput} autoComplete="given-name" />
                    </Field>
                    <Field label="Last Name" htmlFor="ln" error={errors.lastName} span="half">
                      <input id="ln" data-testid="info-lastname" value={form.lastName} onChange={set('lastName')} className={baseInput} autoComplete="family-name" />
                    </Field>
                    <Field label="Email" htmlFor="em" error={errors.email} span="half">
                      <input id="em" type="email" data-testid="info-email" value={form.email} onChange={set('email')} className={baseInput} autoComplete="email" />
                    </Field>
                    <Field label="Phone" htmlFor="ph" error={errors.phone} span="half">
                      <input id="ph" type="tel" data-testid="info-phone" value={form.phone} onChange={set('phone')} className={baseInput} autoComplete="tel" />
                    </Field>
                    <Field label="Date of Birth" htmlFor="dob" span="half">
                      <input id="dob" type="date" data-testid="info-dob" value={form.dob} onChange={set('dob')} className={`${baseInput} [color-scheme:dark]`} autoComplete="bday" />
                    </Field>
                  </div>
                </div>

                {/* ADDRESS */}
                <div>
                  <h3 className="text-[11px] font-bold tracking-[0.3em] text-red-500 uppercase mb-4">Billing Address</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Street Address" htmlFor="st" error={errors.street}>
                      <input id="st" data-testid="info-street" value={form.street} onChange={set('street')} className={baseInput} autoComplete="street-address" />
                    </Field>
                    <Field label="City" htmlFor="ci" error={errors.city} span="half">
                      <input id="ci" data-testid="info-city" value={form.city} onChange={set('city')} className={baseInput} autoComplete="address-level2" />
                    </Field>
                    <Field label="State" htmlFor="stt" error={errors.state} span="half">
                      <select id="stt" data-testid="info-state" value={form.state} onChange={set('state')} className={baseInput} autoComplete="address-level1">
                        <option value="">Select…</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="ZIP Code" htmlFor="zp" error={errors.zip} span="half">
                      <input id="zp" data-testid="info-zip" value={form.zip} onChange={set('zip')} className={baseInput} autoComplete="postal-code" />
                    </Field>
                  </div>
                </div>

                {/* EMERGENCY */}
                <div>
                  <h3 className="text-[11px] font-bold tracking-[0.3em] text-red-500 uppercase mb-1">Emergency Contact</h3>
                  <p className="text-xs text-zinc-500 mb-4">Optional — helps our trainers in the rare case of an incident.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Contact Name" htmlFor="emn" span="half">
                      <input id="emn" data-testid="info-em-name" value={form.emName} onChange={set('emName')} className={baseInput} />
                    </Field>
                    <Field label="Contact Phone" htmlFor="emp" span="half">
                      <input id="emp" type="tel" data-testid="info-em-phone" value={form.emPhone} onChange={set('emPhone')} className={baseInput} />
                    </Field>
                    <Field label="Relationship" htmlFor="emr" span="half">
                      <input id="emr" data-testid="info-em-rel" value={form.emRel} onChange={set('emRel')} className={baseInput} placeholder="e.g. spouse, parent" />
                    </Field>
                  </div>
                </div>

                {/* PAYMENT */}
                <div>
                  <h3 className="text-[11px] font-bold tracking-[0.3em] text-red-500 uppercase mb-1">Payment Details</h3>
                  <p className="text-xs text-zinc-500 mb-4">
                    We store only the last 4 digits + expiration for billing reference. Full card capture happens at the front desk on your first visit.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Cardholder Name" htmlFor="ch" error={errors.cardholder}>
                      <input id="ch" data-testid="info-cardholder" value={form.cardholder} onChange={set('cardholder')} className={baseInput} autoComplete="cc-name" />
                    </Field>
                    <Field label="Card Number" htmlFor="cn" error={errors.cardNumber}>
                      <input id="cn" data-testid="info-card-number" inputMode="numeric" value={form.cardNumber} onChange={onCardNumber} className={`${baseInput} font-mono tracking-wider`} placeholder="•••• •••• •••• ••••" autoComplete="cc-number" />
                    </Field>
                    <Field label="Expiration (MM/YY)" htmlFor="ce" error={errors.cardExp} span="half">
                      <input id="ce" data-testid="info-card-exp" inputMode="numeric" value={form.cardExp} onChange={onCardExp} className={`${baseInput} font-mono`} placeholder="MM/YY" autoComplete="cc-exp" />
                    </Field>
                    <Field label="CVV" htmlFor="cv" error={errors.cardCvv} span="half">
                      <input id="cv" data-testid="info-card-cvv" inputMode="numeric" value={form.cardCvv} onChange={onCardCvv} className={`${baseInput} font-mono`} placeholder="•••" autoComplete="cc-csc" />
                    </Field>
                    <Field label="Billing ZIP" htmlFor="bz" error={errors.billingZip} span="half">
                      <input id="bz" data-testid="info-billing-zip" value={form.billingZip} onChange={set('billingZip')} className={baseInput} />
                    </Field>
                  </div>
                </div>

                {/* TERMS */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="info-agree" checked={form.agree} onChange={set('agree')} className="mt-0.5 w-4 h-4 accent-red-600" />
                    <span className="text-sm text-zinc-300 leading-relaxed">
                      I agree to the FitnessPro Galt membership terms, including the {plan.monthlyDues > 0 ? 'monthly auto-renewal' : 'one-time annual payment'} and cancellation policy. I am 18+ or have my parent/guardian's consent.
                    </span>
                  </label>
                  {errors.agree && <p className="mt-1 text-xs text-red-400 pl-7">{errors.agree}</p>}
                </div>

                <div className="border-t border-red-900/40 pt-5 flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.3em] font-bold text-red-500 uppercase">Total Due Today</span>
                  <span className="font-display text-3xl tabular-nums text-white">
                    {money(selections?.dueToday ?? (plan.enrollmentFee + effectivePricing(plan, members).signUpDues))}
                  </span>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 z-40"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="info-submit"
          className="block w-full bg-red-600 hover:bg-red-500 disabled:bg-zinc-700 text-white py-3.5 sm:py-5 font-bold transition shadow-[0_-8px_30px_rgba(239,43,43,0.35)]"
        >
          {submitting ? (
            <span className="text-[11px] sm:text-base tracking-[0.14em] sm:tracking-[0.3em]">SUBMITTING…</span>
          ) : (
            <span className="flex items-center justify-center gap-2.5 sm:gap-3">
              <span className="text-[11px] sm:text-base tracking-[0.14em] sm:tracking-[0.3em]">
                <span className="sm:hidden">ENROLL NOW</span>
                <span className="hidden sm:inline">COMPLETE ENROLLMENT</span>
              </span>
              <span
                data-testid="info-submit-total"
                className="tabular-nums font-display text-[15px] sm:text-lg leading-none px-2 py-1 rounded bg-black/30 backdrop-blur-sm border border-white/15 whitespace-nowrap"
              >
                {money(liveDueToday)}
              </span>
              <span aria-hidden="true" className="text-base sm:text-lg leading-none">→</span>
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
